import React, { useMemo } from 'react';

import { FormValues, IJSONSchemaProperty, SchemaType } from '../../integration-test-modal.types';
import { schemaUtils } from '../../integration-test-modal.utils';

import { BooleanField } from './fields/BooleanField';
import { EnumSelectField } from './fields/EnumSelectField';
import { JSONField } from './fields/JSONField';
import { NestedObjectField } from './fields/NestedObjectField';
import { NumberField } from './fields/NumberField';
import { PasswordField } from './fields/PasswordField';
import { TextField } from './fields/TextField';
import { ISchemaFormFieldProps } from './schema-form-field.types';
import { schemaFieldUtils } from './schema-form-field.utils';

export const SchemaFormField: React.FC<ISchemaFormFieldProps> = ({
  name,
  path,
  property,
  value,
  onChange,
  required,
  definitions,
  hideOptionalFields
}) => {
  const resolvedProperty = useMemo((): IJSONSchemaProperty => {
    if (property.$ref) {
      const refProperty = schemaUtils.resolveRef(property.$ref, definitions);
      if (refProperty) {
        return { ...refProperty, title: property.title || refProperty.title };
      }
    }
    return property;
  }, [property, definitions]);

  if (hideOptionalFields && !required) {
    return null;
  }

  if (resolvedProperty.isHidden) {
    return null;
  }

  if (resolvedProperty.type === SchemaType.ARRAY) {
    return (
      <JSONField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  if (schemaFieldUtils.isNestedObjectField(resolvedProperty)) {
    return (
      <NestedObjectField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value as FormValues}
        onChange={onChange}
        required={required}
        definitions={definitions}
        hideOptionalFields={hideOptionalFields}
      />
    );
  }

  if (resolvedProperty.type === SchemaType.OBJECT) {
    return (
      <JSONField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  if (resolvedProperty.enum) {
    return (
      <EnumSelectField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  if (resolvedProperty.type === SchemaType.BOOLEAN) {
    return (
      <BooleanField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  if (schemaFieldUtils.isNumericField(resolvedProperty)) {
    return (
      <NumberField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  if (schemaFieldUtils.isPasswordField(resolvedProperty)) {
    return (
      <PasswordField
        name={name}
        path={path}
        property={resolvedProperty}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  if (schemaFieldUtils.isUnknownObjectField(resolvedProperty, value)) {
    return (
      <JSONField
        name={name}
        path={path}
        property={{ ...resolvedProperty, type: SchemaType.OBJECT }}
        value={value}
        onChange={onChange}
        required={required}
      />
    );
  }

  return (
    <TextField
      name={name}
      path={path}
      property={resolvedProperty}
      value={value}
      onChange={onChange}
      required={required}
    />
  );
};
